import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { termsAgreementSchema } from '../schemas/termsAgreementSchema';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePlans } from '../utils/usePlans';
import { createCheckoutSession } from '../api/checkout';
import UserPageLayout from '../components/UserPageLayout';
import {
  Box,
  Button,
  Typography,
  Stack,
  FormControlLabel,
  Checkbox,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { NoticeModal } from '../components/Modals';

const TermsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeMsg, setNoticeMsg] = useState('');

  const showNotice = (msg) => {
    setNoticeMsg(msg);
    setNoticeOpen(true);
  };
  const { userId, companyId, price_id } = location.state || {};

  const { subscriptionOptions, loading: plansLoading } = usePlans();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = useForm({
    resolver: zodResolver(termsAgreementSchema),
    defaultValues: {
      agreed: false,
    },
  });

  const agreed = watch('agreed');

  const onSubmit = async (data) => {
    const token = localStorage.getItem('token');
    if (!token) {
      showNotice('ログインしてください');
      navigate('/');
      return;
    }

    if (!companyId || !price_id) {
      showNotice('会社情報が正しく設定されていません');
      return;
    }

    // subscriptionOptionsが読み込まれているか確認
    if (plansLoading) {
      showNotice(
        'プラン情報を読み込み中です。少し待ってから再度実行してください。'
      );
      return;
    }

    // 価格IDがサブスクか判定
    const mode = subscriptionOptions.some((sub) => sub.price_id === price_id)
      ? 'subscription'
      : 'payment';
    const trialDays =
      subscriptionOptions.find((sub) => sub.price_id === price_id)
        ?.trial_days || null;

    try {
      const res = await createCheckoutSession(
        companyId,
        price_id,
        mode,
        trialDays,
        null,
        userId
      );
      window.location.href = res.checkout_url;
    } catch (err) {
      console.error('契約処理に失敗', err);
      showNotice('契約処理に失敗しました');
    }
  };

  if (plansLoading) {
    return (
      <UserPageLayout title="利用規約" maxWidth="md">
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1">プラン情報を読み込み中...</Typography>
        </Box>
      </UserPageLayout>
    );
  }

  const termsSections = [
    {
      title: '契約期間',
      items: [
        '・本サービスは申込完了後から利用可能となり、解約をしない限り契約は継続し、毎月、月額利用料をお支払いいただきます。申込完了日から翌月の同日までを1か月間とします。ただし、翌月の同日が存在しない場合は、翌月末日とします。',
        '・本手続完了後のお客さまのご都合によるキャンセル・返金についてはお受けできませんのでご注意下さい。',
      ],
    },
    {
      title: 'お支払いについて',
      items: [
        '・クレジットカード払い',
        '・月額利用料金は前払いとなります。',
        '・お支払い時期はご利用のクレジットカードのご契約内容を確認下さい。',
        'なお、本お申し込み時に各カード発行会社の認証を行います。',
        '・Stripeでの決済では、本人認証（3Dセキュア）が必須となっております。',
        '決済ができない場合は、ご利用のカード会社で3Dセキュアの設定が有効になっているかご確認ください。',
        'お客さまから収集した以下の個人情報は、カード発行会社が行う不正利用検知・防止のために、お客さまが利用されているカード発行会社へ提供させていただきます。',
        '・カード番号 ・カード名義 ・電話番号　・eメールアドレス　・インターネット利用環境に関する情報 等',
        'お客さまが利用されているカード発行会社が外国にある場合、これらの情報は当該発行会社が所属する国に移転される場合があります。',
        'お客さまが未成年の場合、親権者または後見人の承諾を得たうえで、本サービスを利用するものとします。',
      ],
    },
    {
      title: '解約方法について',
      items: ['・本サイトのマイページでお手続きいただけます。'],
    },
    {
      title: 'Leminoに関する注意事項',
      items: [
        '・ご利用にあたっては、株式会社NTTドコモの定める規約/特約が適用されます。',
        '・Leminoのシリアルコード発行および、ご利用にあたってはdアカウントが必要となります。',
        '・一部のコンテンツは個別課金となります。',
        '・コンテンツの視聴(一部を除きストリーミング形式による配信となります)などには別途パケット通信料がかかります。',
        '※コンテンツをご利用の際、データ量が多くなり、Xi・5G通信においては比較的短時間で速度制限条件に達する場合があります。速度制限下での本サービスの視聴は困難ですので、無線LAN(Wi-Fi)でのご利用をおすすめします。',
        '・システム及び通信の障害、保守等により一時的に利用できない場合がございます。デバイスによっては一部視聴できないコンテンツがあります。',
        '・海外ではコンテンツの視聴はできません。',
        '・権利者の都合などにより、予告なくコンテンツの配信が中止される場合があります。',
        '・Leminoで実施しているキャンペーン等が適用されない場合がございます。',
        '・対応機種、ご利用条件など詳しくはドコモのホームページでご確認ください。',
      ],
    },
  ];

  return (
    <UserPageLayout title="利用規約" maxWidth="md">
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          maxWidth: 'none',
          boxShadow: 'none',
          minHeight: 'fit-content',
          padding: 0,
          mt: '5dvh',
          alignItems: 'flex-start',
        }}
      >
        <Paper
          elevation={1}
          sx={{
            p: 1,
            mb: 3,
            maxHeight: '400px',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              maxHeight: '320px',
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.5)',
                },
              },
            }}
          >
            <List dense>
              {termsSections.map((section, sectionIndex) => (
                <Box key={sectionIndex} sx={{ mb: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      color: 'primary.main',
                      p: 2,
                    }}
                  >
                    {section.title}
                  </Typography>
                  {section.items.map((item, itemIndex) => (
                    <ListItem key={itemIndex} sx={{ py: 0.5, pl: 2 }}>
                      <ListItemText primary={`${item}`} />
                    </ListItem>
                  ))}
                </Box>
              ))}
            </List>
          </Box>
        </Paper>

        <Box sx={{ mb: 3, textAlign: 'center', width: '100%' }}>
          <FormControlLabel
            control={
              <Checkbox
                {...register('agreed')}
                checked={agreed}
                color="primary"
              />
            }
            label="利用規約に同意します"
            sx={{ alignItems: 'center' }}
          />
        </Box>

        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/mypage')}
            sx={{ borderRadius: 1, flex: 1 }}
            disabled={isSubmitting}
          >
            戻る
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!agreed || isSubmitting}
            sx={{ borderRadius: 1, flex: 1 }}
          >
            {isSubmitting ? '処理中...' : '同意して決済画面に進む'}
          </Button>
        </Stack>
      </Box>
      <NoticeModal
        open={noticeOpen}
        message={noticeMsg}
        onClose={() => setNoticeOpen(false)}
      />
    </UserPageLayout>
  );
};

export default TermsPage;
